import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { PlusIcon, PencilIcon, Trash2Icon } from "lucide-react"

interface Country {
  id: string
  name: string
  isoCode: string
  code: string | null
  createdAt: string
}

const EMPTY = { name: "", isoCode: "", code: "" }
type CountryPayload = { name: string; isoCode: string; code: string | null }

export default function CountriesPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [sheetOpen,  setSheetOpen]  = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editing,    setEditing]    = useState<Country | null>(null)
  const [deleting,   setDeleting]   = useState<Country | null>(null)
  const [form,       setForm]       = useState({ ...EMPTY })
  const [formError,  setFormError]  = useState<string | null>(null)

  // ── Query ──────────────────────────────────────────────────────────────────
  const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

  const { data: countries = [], isLoading } = useQuery({
    queryKey: ["geo", "countries"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/countries`)
      return res.json() as Promise<Country[]>
    },
  })

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: CountryPayload) => {
      const res = await api.geo.countries.$post({ json: data })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "countries"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CountryPayload }) => {
      const res = await api.geo.countries[":id"].$put({ param: { id }, json: data })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "countries"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.geo.countries[":id"].$delete({ param: { id } })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "countries"] }); setDeleteOpen(false); setDeleting(null) },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  function closeSheet() { setSheetOpen(false); setEditing(null); setForm({ ...EMPTY }); setFormError(null) }

  function openCreate() { setEditing(null); setForm({ ...EMPTY }); setFormError(null); setSheetOpen(true) }

  function openEdit(c: Country) {
    setEditing(c); setForm({ name: c.name, isoCode: c.isoCode, code: c.code ?? "" }); setFormError(null); setSheetOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setFormError(null)
    const payload = {
      ...form,
      isoCode: form.isoCode.toUpperCase(),
      code: form.code.trim().toUpperCase() || null,
    }
    if (editing) updateMut.mutate({ id: editing.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  return (
    <>
      <SiteHeader title="Países" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            Gestión de países. Usa código ISO 3166-1 alfa-2 (ej. AR, BR, CL).
          </p>
          {can("write", "geo") && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo país
            </Button>
          )}
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">ISO</TableHead>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Nombre</TableHead>
                {can("write", "geo") && <TableHead className="text-right pr-4 w-24">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-16 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : countries.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-16 text-muted-foreground">No hay países registrados</TableCell></TableRow>
              ) : countries.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{c.isoCode}</span>
                  </TableCell>
                  <TableCell>
                    {c.code
                      ? <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{c.code}</span>
                      : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  {can("write", "geo") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon-sm" onClick={() => openEdit(c)} title="Editar">
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon-sm" onClick={() => { setDeleting(c); setDeleteOpen(true) }}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Eliminar">
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar país" : "Nuevo país"}</SheetTitle>
            <SheetDescription>Nombre completo y código ISO 3166-1 alfa-2.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4">
            <div className="grid gap-1.5">
              <Label htmlFor="c-name">Nombre</Label>
              <Input id="c-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="c-iso">Código ISO</Label>
              <Input id="c-iso" value={form.isoCode} maxLength={2} placeholder="AR" className="uppercase"
                onChange={e => setForm(f => ({ ...f, isoCode: e.target.value.toUpperCase() }))} required />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="c-code">Código ucarga</Label>
              <Input id="c-code" value={form.code} maxLength={10} placeholder="NA" className="uppercase"
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
              <p className="text-xs text-muted-foreground">Código interno del sistema ucarga (opcional).</p>
            </div>
            {formError && <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>}
            <SheetFooter className="px-0 mt-2">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear país"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar país</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{deleting?.name}</strong>? Se eliminarán también todas sus provincias y ciudades en cascada.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button></DialogClose>
            <Button variant="destructive" disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}>
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
